import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { achievements } from '@/data/achievements';
import type { UserProgress } from './useProgress';
import { dispatchMilestone } from '@/components/milestones/MilestoneCelebration';

export function useAchievements(progress: UserProgress) {
  const { user } = useAuth();
  const earnedRef = useRef<Set<string>>(new Set());
  const loadedRef = useRef(false);

  // Load already-earned achievements from DB
  useEffect(() => {
    if (!user) return;
    supabase
      .from('user_achievements')
      .select('achievement_id')
      .eq('user_id', user.id)
      .then(({ data }) => {
        if (data) {
          earnedRef.current = new Set(data.map(r => r.achievement_id));
        }
        loadedRef.current = true;
      });
  }, [user]);

  // Check for newly earned achievements whenever progress changes
  const checkAchievements = useCallback(() => {
    if (!user || !loadedRef.current) return;

    for (const achievement of achievements) {
      if (earnedRef.current.has(achievement.id)) continue;
      if (achievement.check(progress)) {
        earnedRef.current.add(achievement.id);
        // Save via edge function (service_role enforced)
        supabase.functions
          .invoke('grant-achievement', { body: { achievement_id: achievement.id } })
          .then(() => {});
        // Trigger full-screen celebration
        dispatchMilestone({
          id: `badge-${achievement.id}`,
          kind: 'badge',
          emoji: achievement.icon,
          headline: 'Badge Earned!',
          subline: `${achievement.title} — ${achievement.description}`,
          xp: progress.xp,
        });
      }
    }
  }, [user, progress]);

  useEffect(() => {
    checkAchievements();
  }, [checkAchievements]);

  return {
    earnedIds: earnedRef.current,
  };
}
